/**
 * Org role checks.
 *
 * Backend is adding an "owner" role (org owner, superset of admin). Every FE
 * check that used to compare `role === 'admin'` must treat an owner as an
 * admin too, or an Owner loses every admin UI the day that role ships. Route
 * all org-role admin checks through `isAdminOrOwner` instead of comparing the
 * literal string, so this is the only place that needs to know about "owner".
 *
 * Role is a free string from the backend; matches are case-sensitive, same as
 * the literal comparisons this replaces.
 */

export function isOwner(role: string | null | undefined): boolean {
  return role === 'owner';
}

export function isAdminOrOwner(role: string | null | undefined): boolean {
  return role === 'admin' || isOwner(role);
}

/**
 * Who may use an Owner-only surface, for denied-state copy ("Only Owners can
 * add credits").
 */
export function ownerOnly(action: string): string {
  return `Only Owners can ${action}`;
}

export type OrgRole = 'owner' | 'admin' | 'member';

const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

/**
 * Display label for an org role. Unknown or missing roles return '' rather
 * than a guessed label or the raw backend string.
 */
export function roleLabel(role: string | null | undefined): string {
  return role && Object.hasOwn(ROLE_LABELS, role)
    ? ROLE_LABELS[role as OrgRole]
    : '';
}

/**
 * Roles the viewer may pick in a role dropdown. Only an Owner may grant (or
 * see) Owner; the backend enforces the finer team rules for an Admin.
 *
 * `target`/`viewerTeamUUID` decide what a team-scoped Admin may do: move a
 * Member or Admin on their own team between Member and Admin; never touch
 * an Owner or anyone outside their team.
 */
export function assignableRoles(
  viewerRole: string | null | undefined,
  target?: { Role: string | null | undefined; TeamUUID?: string | null },
  viewerTeamUUID?: string | null
): OrgRole[] {
  if (isOwner(viewerRole)) return ['member', 'admin', 'owner'];
  if (
    viewerRole === 'admin' &&
    (target?.Role === 'member' || target?.Role === 'admin') &&
    !!viewerTeamUUID &&
    target.TeamUUID === viewerTeamUUID
  ) {
    return ['member', 'admin'];
  }
  return [];
}

/**
 * Roles the viewer may mint invite links for. A team-scoped Admin invites
 * members only (the backend pins the link to their team); a Member mints none.
 */
export function inviteRoles(
  viewerRole: string | null | undefined
): Array<Exclude<OrgRole, 'owner'>> {
  if (isOwner(viewerRole)) return ['member', 'admin'];
  if (viewerRole === 'admin') return ['member'];
  return [];
}
